<template>
  <EmailFormModal
    v-if="emailFormModalVisible"
    :callerType="EmailFormCallerType.RfrAssignmentsRequest"
    :emailRecipient="emailFormAssignment?.RcEmail"
    :companyName="emailFormAssignment?.CompanyName"
    :plantName="emailFormAssignment?.PlantName"
    :assignmentId="emailFormAssignment?.assignmentId"
    @closeModal="emailFormModalVisible = false"
    open
  />
  <AssignmentSchedulesModal
    v-if="schedulesModalVisible"
    :ownsIds="selectedAssignmentIds"
    @closeModal="schedulesModalVisible = false"
    open
  />
  <AssignmentDetailsModal
    v-if="detailsModalVisible && detailsAssignment"
    :assignment="detailsAssignment"
    @closeModal="detailsModalVisible = false"
  />
  <div class="assignments-table" data-cy="assignments-table">
    <div class="assignments-table-header grid-control__container">
      <Button
        theme-color="primary"
        @click.prevent="getSchedulesClicked"
        :disabled="selectedAssignmentIds.length === 0"
        data-cy="schedules-button"
      >
        Get Schedules for
        {{
          selectedAssignmentIds.length > 0 ? selectedAssignmentIds.length : ""
        }}
        Selected Plants
      </Button>
      <ToggleSwitch
        v-if="isTeamLeader"
        v-model="showTeamAssignments"
        text="Show My Team Assignments"
      />
      <div class="header-right-block">
        <TableSearch
          class="assignments-search"
          :value="searchInput"
          placeholder="Search by Company, Plant, State, Country"
          @searchSubmitted="onSearchSubmitted"
        />
        <ClearFiltersButton @clearFiltersClicked="clearFilters" />
      </div>
    </div>
    <AssignmentsGrid
      :assignments="assignments"
      :loading="assignmentsLoading"
      :skip="assignmentsSkip"
      :pageSize="assignmentsTake"
      :total="totalAssignments"
      :filter="assignmentsFilter"
      :teamAssignments="showTeamAssignments"
      :sort="sortDescriptors"
      :getPropValues="getPropValuesAsync"
      @assignmentsChanged="onAssignmentsChanged"
      @selectionChanged="onSelectionChanged"
      @selectAllClicked="onSelectAllClickedAsync"
      @sortChanged="onSortChanged"
      @sendEmailClicked="onSendEmailClicked"
      @detailsClicked="onDetailsClicked"
    />
    <ColumnMenuIconHandler :filter="assignmentsFilter" />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watch } from "vue";
import AssignmentsGrid from "./AssignmentsGrid.vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import type { GridFilterChangeEvent } from "@progress/kendo-vue-grid";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";

import {
  getValueFilterDistinct,
  getJsonFilterString,
  FilterValuesTake,
} from "@/shared/helpers/filtersHelper";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type {
  AssignmentWithSelection,
  GetRfrAssigmentsWithFilteringRequest,
} from "@/api/rfrAssignments";
import EmailFormModal from "@/shared/ui/modal/EmailFormModal.vue";
import { EmailFormCallerType } from "@/Domain/EmailFormCallerType";
import AssignmentSchedulesModal from "./AssignmentSchedulesModal.vue";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import AssignmentDetailsModal from "./AssignmentDetailsModal.vue";
import ToggleSwitch from "@/shared/ui/switch/ToggleSwitch.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();
const urlParams = new URLSearchParams(window.location.search);

const searchInput = ref<string>("");
const assignments = ref<AssignmentWithSelection[]>([]);
const totalAssignments = ref<number>(0);
const assignmentsLoading = ref<boolean>(false);
const assignmentsSkip = ref<number>(0);
const assignmentsFilter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});
const selectedAssignmentIds = ref<number[]>([]);
const assignmentsTake = ref<number>(25);
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);
const showTeamAssignments = ref<boolean>(
  urlParams.get("mode")?.toLowerCase() === "team"
);
const isTeamLeader = ref<boolean>(false);

const emailFormModalVisible = ref<boolean>(false);
const emailFormAssignment = ref<AssignmentWithSelection>(
  {} as AssignmentWithSelection
);

const schedulesModalVisible = ref<boolean>(false);

const detailsModalVisible = ref<boolean>(false);
const detailsAssignment = ref<AssignmentWithSelection>();

const tableName = "RfrAssignments";

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const getRfrAssignments = async function (
  skip: number,
  take: number,
  filter: CompositeFilterDescriptor | null = null,
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    searchValue: searchInput.value,
    onlyMainCompanies: true,
    filters: getJsonFilterString(filter),
    skip: skip,
    take: take,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
    teamLeaderMode: showTeamAssignments.value,
  } as GetRfrAssigmentsWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetRfrAssigmentsWithFiltering", requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetRfrAssigmentsWithFiltering", requestParams)
  );
};

const clearFilters = function () {
  searchInput.value = "";
  assignmentsFilter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  sortField.value = "";
  sortAscending.value = false;
  updateRfrAssignmentsAsync();
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  updateRfrAssignmentsAsync(assignmentsFilter.value);
};

const onAssignmentsChanged = function (
  skip: number,
  take: number,
  filterEvent: GridFilterChangeEvent | null
) {
  // Kendo Grid sending two duplicate events on filter change
  // If filterEvent.filter is null, it's the second event and we should ignore it.
  // FilterEvent is null when user change page size or page number
  // And in that case only one event is sent
  if (filterEvent) {
    if (!filterEvent.filter) return;
    assignmentsFilter.value.filters = filterEvent.filter.filters;
  }
  if (assignmentsTake.value != take) {
    assignmentsTake.value = take;
    GridOptionsService.savePageSizeAsync(tableName, take);
  }
  assignmentsSkip.value = skip;
  updateRfrAssignmentsAsync(assignmentsFilter.value);
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  assignmentsSkip.value = 0;
  updateRfrAssignmentsAsync(assignmentsFilter.value);
};

const onSendEmailClicked = function (assignment: AssignmentWithSelection) {
  emailFormAssignment.value = assignment;
  emailFormModalVisible.value = true;
};

const onDetailsClicked = function (assignment: AssignmentWithSelection) {
  detailsAssignment.value = assignment;
  detailsModalVisible.value = true;
};

const onSelectionChanged = function (
  selectedAssignments: AssignmentWithSelection[]
) {
  selectedAssignments.forEach((assignment) => {
    const index = selectedAssignmentIds.value.findIndex(
      (p) => p === assignment.ownsId
    );
    if (assignment.selected) {
      if (index === -1) selectedAssignmentIds.value.push(assignment.ownsId);
      else
        console.debug(
          `AssignmentsTable.vue: onSelectionChanged(): assignment already selected`,
          assignment,
          selectedAssignmentIds.value[index]
        );
    } else if (index > -1) {
      selectedAssignmentIds.value.splice(index, 1);
    }
  });
};

const onSelectAllClickedAsync = async function (checked: boolean) {
  if (!checked) {
    selectedAssignmentIds.value = [];
    return;
  }

  await handleResponsePromiseAsync(
    getRfrAssignments(0, 999999, assignmentsFilter.value, "ownsId"),
    (data) => {
      selectedAssignmentIds.value = data.ListItems.map((i) => i.ownsId);
    },
    "AssignmentsTable.vue",
    "onSelectAllClickedAsync"
  );
};

const getSchedulesClicked = function () {
  schedulesModalVisible.value = true;
};

const updateRfrAssignmentsAsync = async function (
  filter: CompositeFilterDescriptor | null = null
) {
  if (assignmentsLoading.value) return;
  try {
    assignmentsLoading.value = true;
    let result = await getRfrAssignments(
      assignmentsSkip.value,
      assignmentsTake.value,
      filter
    );
    if (result.success) {
      assignments.value = result.response.ListItems;
      assignments.value.forEach((a) => {
        a.selected = selectedAssignmentIds.value.some(
          (sa) => sa === a.assignmentId
        );
      });
      totalAssignments.value = result.response.TotalItemsInList;
    } else {
      console.error(
        "AssignmentsTable.vue: updateRfrAssignmentsAsync(): resp = " +
          result
      );
    }
    console.debug(
      `AssignmentsTable.vue: updateRfrAssignmentsAsync(), result:`,
      result
    );
  } catch (err) {
    console.error(
      "AssignmentsTable.vue: updateRfrAssignmentsAsync(): catch error = " + err
    );
  } finally {
    assignmentsLoading.value = false;
  }
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: AssignmentWithSelection[] = [];
  if (!column.field) return result;

  await handleResponsePromiseAsync(
    getRfrAssignments(0, 999999, null, column.field),
    (data) => {
      result = data.ListItems;
      result = getValueFilterDistinct(column.field!, result);
    },
    "AssignmentsTable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const updateIsTeamLeaderValueAsync = async function () {
  const params = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
  };
  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey(params, "IsRfrTeamLeader"),
      () => api.get("IsRfrTeamLeader", params)
    ),
    (data) => {
      isTeamLeader.value = data;
      if (!data) showTeamAssignments.value = false;
    },
    "AssignmentsTable.vue",
    "updateIsTeamLeaderValueAsync"
  );
};

watch(
  () => showTeamAssignments.value,
  () => {
    updateRfrAssignmentsAsync(assignmentsFilter.value);
    urlParams.set("mode", showTeamAssignments.value ? "team" : "my");
    window.history.pushState(
      {},
      "",
      `${window.location.pathname}?${urlParams}`
    );
  }
);

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) assignmentsTake.value = pageSize;
  await updateIsTeamLeaderValueAsync();
  updateRfrAssignmentsAsync();
});
</script>

<style lang="scss">
.assignments-table {
  margin: 0 24px 24px;

  .assignments-table-header {
    display: flex;
    gap: 12px;

    .header-right-block {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      .assignments-search {
        width: 400px;
        margin-right: 16px;
      }
    }
  }
}
</style>
